import axios from "axios";
import { AppThunk } from "store/store";
import { API_ENDPOINTS } from "config/apiConfig";
import { axiosInstance } from "config/axiosConfig";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { hideLoading, showLoading } from "./loadingSlice";
import {
  TransactionParams,
  TransactionInfo,
  Transaction,
  TransactionNotificationParams,
} from "common/interfaces";

interface TransactionState {
  transactionData: {
    data: Transaction[];
    message: string;
    success: boolean;
  } | null;
  transactionDetailData: {
    data: Transaction;
    message: string;
    success: boolean;
  } | null;
  updateTransactionData: {
    message: string;
    success: boolean;
  } | null;
  transactionNotificationData: TransactionNotificationParams | null;
  error: string | null;
}

const initialState: TransactionState = {
  transactionNotificationData: null,
  transactionData: null,
  transactionDetailData: null,
  updateTransactionData: null,
  error: null,
};

export const transactionSlice = createSlice({
  name: "transaction",
  initialState,
  reducers: {
    transactionSuccess: (state, action: PayloadAction<any>) => {
      state.transactionData = action.payload;
      state.error = null;
    },
    transactionFail: (state, action: PayloadAction<string>) => {
      state.transactionData = null;
      state.error = action.payload;
    },
    transactionDetailSuccess: (state, action: PayloadAction<any>) => {
      state.transactionDetailData = action.payload;
      state.error = null;
    },
    transactionDetailFail: (state, action: PayloadAction<string>) => {
      state.transactionDetailData = null;
      state.error = action.payload;
    },
    updateTransactionStart: (state) => {
      state.error = null;
    },
    updateTransactionSuccess: (state, action: PayloadAction<any>) => {
      state.updateTransactionData = action.payload;
      state.error = null;
    },
    updateTransactionFail: (state, action: PayloadAction<string>) => {
      state.updateTransactionData = null;
      state.error = action.payload;
    },
    transactionNotificationSuccess: (
      state,
      action: PayloadAction<TransactionNotificationParams>,
    ) => {
      state.transactionNotificationData = action.payload;
      state.error = null;
    },
    transactionNotificationFail: (state, action: PayloadAction<string>) => {
      state.transactionNotificationData = null;
      state.error = action.payload;
    },
    transactionCleanData: (state) => {
      state.transactionData = null;
      state.transactionDetailData = null;
      state.updateTransactionData = null;
      state.transactionNotificationData = null;
      state.error = null;
    },
  },
});

export const {
  transactionSuccess,
  transactionFail,
  transactionDetailSuccess,
  transactionDetailFail,
  updateTransactionSuccess,
  updateTransactionFail,
  transactionCleanData,
  transactionNotificationSuccess,
  transactionNotificationFail,
} = transactionSlice.actions;

export const fetchTransactions =
  (productId: number | undefined): AppThunk =>
  async (dispatch) => {
    const controller = new AbortController();
    dispatch(showLoading());
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.TRANSACTION}/${productId}`,
        {
          signal: controller.signal,
        },
      );
      dispatch(transactionSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(transactionFail(error.message || "Something went wrong"));
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export const fetchTransactionDetail =
  (transactionParams: TransactionParams): AppThunk =>
  async (dispatch) => {
    const controller = new AbortController();
    dispatch(showLoading());
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.TRANSACTION_DETAIL,
        {
          params: transactionParams,
          signal: controller.signal,
        },
      );
      dispatch(transactionDetailSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(
          transactionDetailFail(error.message || "Something went wrong"),
        );
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export const updateTransaction =
  (TransactionInfo: TransactionInfo): AppThunk =>
  async (dispatch) => {
    const controller = new AbortController();
    dispatch(showLoading());
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.UPDATE_TRANSACTION,
        TransactionInfo,
        { signal: controller.signal },
      );
      dispatch(updateTransactionSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(
          updateTransactionFail(error.message || "Something went wrong"),
        );
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export const fetchTransactionNotification =
  (): AppThunk => async (dispatch) => {
    const controller = new AbortController();
    dispatch(showLoading());
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.TRANSACTION_NOTIFICATION,
        {
          signal: controller.signal,
        },
      );
      dispatch(transactionNotificationSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(
          transactionNotificationFail(error.message || "Something went wrong"),
        );
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export default transactionSlice.reducer;
